import type { QuestionAgentRate } from '@compass/client/types/analytics';
import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { GenericTable } from '@/components/GenericTable';
import { formatPercent1, formatPotentialPp } from '@/pages/analytics/utils/questionDeepDive';
import { buildAnalyticsAgentDetailPath } from '@/pages/analytics/utils/resolveOrg';
import type { SortState, TableColumnConfig, TableConfig } from '@/types/table';

import { ChartCard } from './ChartCard';
import type { ChartDownloadFormat } from './ChartDownloadMenu';

interface QuestionAgentBreakdownTableProps {
  organizationId: string;
  rows: QuestionAgentRate[];
  loading?: boolean;
  totalAgents: number;
  onDownload: (format: ChartDownloadFormat) => void | Promise<void>;
}

const AGENT_COLUMN_WIDTH = 360;

// Sortable fields: miss rate / missed-total / avg score / potential. Default is
// Potential (pp) desc — the biggest coaching lift first.
type AgentSortField = 'miss_rate' | 'missed_total' | 'avg_score' | 'potential_pp';
const DEFAULT_SORT: SortState = { field: 'potential_pp', direction: 'desc' };
const isSortField = (f: string): f is AgentSortField =>
  f === 'miss_rate' || f === 'missed_total' || f === 'avg_score' || f === 'potential_pp';

const sortValue = (row: QuestionAgentRate, field: AgentSortField): number => {
  switch (field) {
    case 'miss_rate':
      return row.miss_rate ?? 0;
    case 'avg_score':
      return row.avg_score ?? 0;
    case 'potential_pp':
      return row.potential_pp ?? 0;
    default:
      return row.missed_count ?? 0;
  }
};

export const QuestionAgentBreakdownTable: React.FC<QuestionAgentBreakdownTableProps> = ({
  organizationId,
  rows,
  loading,
  totalAgents,
  onDownload,
}) => {
  const [searchParams] = useSearchParams();
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT);

  const columns: TableColumnConfig<QuestionAgentRate>[] = useMemo(
    () => [
      {
        key: 'agent_name',
        header: 'Agent',
        width: AGENT_COLUMN_WIDTH,
        accessor: item => (
          <span className="block truncate font-medium text-foreground">
            {item.agent_name || item.agent_id}
          </span>
        ),
      },
      {
        key: 'miss_rate',
        header: 'Miss rate',
        sortable: true,
        sortField: 'miss_rate',
        accessor: item => (
          <span className="font-semibold tabular-nums text-destructive">
            {formatPercent1(item.miss_rate ?? 0)}
          </span>
        ),
      },
      {
        key: 'missed_total',
        header: 'Missed / Total',
        sortable: true,
        sortField: 'missed_total',
        accessor: item => (
          <span className="tabular-nums text-muted-foreground">
            {item.missed_count ?? 0} / {item.total_count ?? 0}
          </span>
        ),
      },
      {
        key: 'avg_score',
        header: 'Avg score',
        sortable: true,
        sortField: 'avg_score',
        accessor: item => (
          <span className="tabular-nums">{formatPercent1(item.avg_score ?? 0)}</span>
        ),
      },
      {
        key: 'potential_pp',
        header: 'Improvement Potential',
        sortable: true,
        sortField: 'potential_pp',
        accessor: item => (
          <span className="tabular-nums text-success">
            {formatPotentialPp(item.potential_pp ?? 0)}
          </span>
        ),
      },
    ],
    [],
  );

  const sortedRows = useMemo(() => {
    const field = isSortField(sort.field) ? sort.field : 'potential_pp';
    const dir = sort.direction === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const delta = sortValue(a, field) - sortValue(b, field);
      if (delta !== 0) return delta * dir;
      // Stable tiebreak by missed count desc so equal primary values stay useful.
      return (b.missed_count ?? 0) - (a.missed_count ?? 0);
    });
  }, [rows, sort]);

  const config: TableConfig<QuestionAgentRate> = useMemo(
    () => ({
      columns,
      // Rows link to the agent's detail page, carrying the current filters.
      getRowLink: item => {
        const query = searchParams.toString();
        const detailPath = buildAnalyticsAgentDetailPath(organizationId, item.agent_id ?? '');
        return `${detailPath}${query ? `?${query}` : ''}`;
      },
      getRowKey: item => item.agent_id ?? item.agent_name ?? '',
      emptyState: {
        title: 'No agents missed this question',
        description: 'No agent has missed this question for the current filters.',
      },
    }),
    [columns, organizationId, searchParams],
  );

  const handleSortChange = (next: SortState[]) => {
    const latest = [...next].reverse().find(s => isSortField(s.field));
    setSort(latest ?? DEFAULT_SORT);
  };

  return (
    <ChartCard
      title="Agent Breakdown"
      info="Which agents are failing this question, their overall score, and how much coaching this one question could lift them. Click a row to see the agent's performance and their evaluations."
      onDownload={format => onDownload(format)}
      downloadDisabled={totalAgents === 0}
      bodyClassName="px-4 pb-4 pt-0 space-y-2"
    >
      <p className="text-xs text-muted-foreground">
        Click a row to review that agent&apos;s missed calls for coaching.
      </p>
      <GenericTable<QuestionAgentRate>
        config={config}
        data={sortedRows}
        loading={loading}
        error={null}
        currentPage={1}
        hasNextPage={false}
        onPageChange={() => {}}
        hidePagination
        sortState={[sort]}
        onSortChange={handleSortChange}
      />
    </ChartCard>
  );
};
